import React, { useEffect, useState } from "react";
import "./../../../assets/css/purchase/form.css";

// React DateTime
import moment from "moment";

// Components
import Breadcrumbs from "../../components/Breadcrumbs";
import Sidebar from "../../components/Sidebar";
import DatePicker from "../../components/DatePicker";

// Utils
import { getAlert, getDataByQuery, getCompany } from "../../../utils/SamuiUtils";
import { ExportBillReportNew } from "../../../utils/ExportUtils";

const BillReportUnpaidSummary = () => {
  const [name, setName] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const loadCompanyInfo = async () => {
    try {
      const compIdFromStorage = window.localStorage.getItem("company") || "1";
      const response = await getCompany();
      if (response && Array.isArray(response)) {
        const matched = response.find(
          (item) => String(item.Comp_id ?? item.Comp_Id) === String(compIdFromStorage)
        );
        setCompanyInfo(matched || null);
        return matched || null;
      }
      setCompanyInfo(null);
      return null;
    } catch (error) {
      console.error("Load company info failed:", error);
      return null;
    }
  };

  useEffect(() => {
    initialize();
    loadCompanyInfo();
  }, []);

  const initialize = async () => {
    setName("15. รายงานใบวางบิล (บิลค้างรับ ตามลูกค้า - แบบสรุป)");
    setSearchCriteria({
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    });
    setIsExporting(false);
  };

  const handleChangeDate = (value, name) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : null,
    }));
  };

  const handleExport = async () => {
    const { fromDate, toDate } = searchCriteria || {};

    if (!fromDate || !toDate) {
      getAlert("FAILED", "กรุณาเลือกช่วงวันที่ให้ครบถ้วน");
      return;
    }

    const fromMoment = moment(fromDate, "YYYY-MM-DD", true);
    const toMoment = moment(toDate, "YYYY-MM-DD", true);

    if (!fromMoment.isValid() || !toMoment.isValid()) {
      getAlert("FAILED", "รูปแบบวันที่ไม่ถูกต้อง");
      return;
    }

    if (fromMoment.isAfter(toMoment)) {
      getAlert("FAILED", "วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด");
      return;
    }

    try {
      setIsExporting(true);

      const companyIdRaw = window.localStorage.getItem("company");
      const parsedCompId = Number(companyIdRaw);
      const compId = Number.isFinite(parsedCompId) ? parsedCompId : 1;

      const from = fromMoment.format("YYYY-MM-DD");
      const to = toMoment.format("YYYY-MM-DD");

      const query = `
        SELECT 
          FORMAT(CAST(H.Doc_Date AS DATETIME), 'dd/MM/yyyy') AS Doc_Date,
          H.Doc_No,
          FORMAT(CAST(H.Doc_Due_Date AS DATETIME), 'dd/MM/yyyy') AS Doc_Due_Date,
          H.Doc_Remark1,
          H.Doc_Remark2,
          H.BL_Total,
          H.BL_Dis,
          H.BL_Vat,
          H.BL_Net_Total,
          H.Pay_Total,
          H.Discount_Value,
          H.Discount_Value_Type,
          H.Doc_Status,
          H.Doc_Paid_Status,
          H.Doc_Status_Paid_Type,
          H.Doc_BL_Receive_Date,
          H.DocStatusPaid_Type_Name,
          H.AR_Code,
          H.AR_Name,
          H.Comp_Id,
          H.Comp_Name_TH,
          H.Comp_Add1,
          H.Comp_Add2,
          H.Comp_Add3,
          H.Comp_Province,
          H.Comp_Post,
          H.Comp_Tel1,
          H.Comp_Tel2,
          H.Comp_TaxNo
        FROM API_1301_BL_H AS H
        WHERE H.Comp_Id = '${compId}'
          AND CAST(H.Doc_Date AS DATE) BETWEEN '${from}' AND '${to}'
        ORDER BY H.Doc_Date ASC, H.Doc_No ASC
      `;

      const response = await getDataByQuery(query);

      if (!response || !Array.isArray(response) || response.length === 0) {
        getAlert("INFO", "ไม่พบข้อมูลใบวางบิลในช่วงวันที่ที่เลือก");
        return;
      }

      const company = companyInfo || (await loadCompanyInfo()) || null;

      await ExportBillReportNew({
        rows: response,
        fromDate: from,
        toDate: to,
        company,
        fileNamePrefix: "ใบวางบิลที่ค้างชำระ",
      });
    } catch (error) {
      console.error("Export Bill Report Error:", error);
      const message = error?.response?.data?.message || error?.message || "ไม่สามารถ Export ข้อมูลได้";
      getAlert("FAILED", message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="main-panel">
        <div className="container">
          <div className="page-inner">
            <div className="page-header d-flex justify-content-between align-items-center">
              <Breadcrumbs
                page={name}
                items={[
                  { name: "รายงาน", url: "/reports" },
                  { name: name, url: "/bill-report-unpaid-summary" },
                ]}
              />
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h4>เงื่อนไขการค้นหา</h4>
                  </div>
                  <div className="card-body">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-3">
                        <label htmlFor="fromDate" className="form-label">
                          ค้นหาวันที่
                        </label>
                        <DatePicker
                          label=""
                          name="fromDate"
                          value={searchCriteria.fromDate}
                          onChange={(date) =>
                            handleChangeDate(date, "fromDate")
                          }
                          disabled={false}
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="toDate" className="form-label">
                          ถึงวันที่
                        </label>
                        <DatePicker
                          label=""
                          name="toDate"
                          value={searchCriteria.toDate}
                          onChange={(date) => handleChangeDate(date, "toDate")}
                          disabled={false}
                        />
                      </div>
                      <div className="col-md-6 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            flex: 1,
                            maxWidth: "180px",
                          }}
                          onClick={() => {
                            const { fromDate, toDate } = searchCriteria || {};
                            if (!fromDate || !toDate) {
                              getAlert("FAILED", "กรุณาเลือกช่วงวันที่ให้ครบถ้วน");
                              return;
                            }

                            const fromMoment = moment(fromDate, "YYYY-MM-DD", true);
                            const toMoment = moment(toDate, "YYYY-MM-DD", true);

                            if (!fromMoment.isValid() || !toMoment.isValid()) {
                              getAlert("FAILED", "รูปแบบวันที่ไม่ถูกต้อง");
                              return;
                            }

                            if (fromMoment.isAfter(toMoment)) {
                              getAlert("FAILED", "วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด");
                              return;
                            }

                            const docId = fromMoment.format("YYYYMMDD");
                            const condition2 = toMoment.format("YYYYMMDD");

                            const fromDisp = fromMoment.format("DD/MM/YYYY");
                            const toDisp = toMoment.format("DD/MM/YYYY");
                            const condition = `วันที่: ${fromDisp} ถึง ${toDisp}, รหัสลูกค้า : ทุกรหัสลูกค้า`;

                            const baseUrl = process.env.REACT_APP_API_REPORT_URL;
                            const href = `${baseUrl}/bi_unpaid?P_DOC_ID=${docId}&P_CONDITION=${encodeURIComponent(condition)}&P_CONDITION2=${condition2}`;

                            const link = document.createElement("a");
                            link.href = href;
                            link.target = "_blank";
                            link.rel = "noopener noreferrer";
                            link.click();
                          }}
                        >
                          <i className="fas fa-print" /> พิมพ์รายงาน
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white me-2"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            flex: 1,
                            maxWidth: "180px",
                          }}
                          onClick={handleExport}
                          disabled={isExporting}
                        >
                          <i className="fas fa-file-excel" /> {isExporting ? "กำลังส่งออก..." : "Export"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                            flex: 1,
                            maxWidth: "150px",
                          }}
                          onClick={() => initialize()}
                        >
                          <i className="fas fa-undo" /> เริ่มใหม่
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillReportUnpaidSummary;
